import { graphql, readInlineData } from 'react-relay';

import { useSupportChatStore } from '../supportChatStore.ts';
import { getPreChatFields } from './getPreChatFields.ts';
import { SELLER_SUPPORT_CHAT_TOUCHPOINTS } from '../constants.ts';

import { initSierraChat, type InitSierraOptions } from '../Sierra/helpers/initSierraChat.ts';

import { type SupportChatTouchpoint, type SettablePreChatFields } from '../types.ts';
import { type initSupportChat_user$key } from './__generated__/initSupportChat_user.graphql.ts';
import { type initSupportChat_seller$key } from './__generated__/initSupportChat_seller.graphql.ts';

const userFragment = graphql`
    fragment initSupportChat_user on User @inline {
        serviceId
        ...initSierraChat_user
    }
`;

const sellerFragment = graphql`
    fragment initSupportChat_seller on Seller @inline {
        serviceId
        ...initSierraChat_seller
    }
`;

type InitSupportChatOptions = Omit<
    InitSierraOptions,
    | 'preChatFields'
    | 'isLiveAgentAvailable'
    | 'setIsInitialized'
    | 'user'
    | 'seller'
    | 'isSellerChat'
> & {
    openWindow: boolean;
    showChatButtonOnLoad: boolean;
    touchpoint: SupportChatTouchpoint;
    preChatFields: SettablePreChatFields;
    user: initSupportChat_user$key | null | undefined;
    seller: initSupportChat_seller$key | null | undefined;
};

function initSupportChat(options: InitSupportChatOptions): void {
    const store = useSupportChatStore.getState();

    if (!store.chatType) {
        return;
    }

    const {
        environment,
        user: userRef,
        seller: sellerRef,
        openWindow,
        showChatButtonOnLoad,
        touchpoint,
        preChatFields: settablePreChatFields,
        locale,
    } = options;

    const user = readInlineData(userFragment, userRef);
    const seller = readInlineData(sellerFragment, sellerRef);

    const isSellerChat = SELLER_SUPPORT_CHAT_TOUCHPOINTS.includes(touchpoint);
    const endUserId = isSellerChat ? seller?.serviceId : user?.serviceId;

    const preChatFields = getPreChatFields({
        touchpoint,
        endUserId,
        isSellerChat,
        preChatFields: settablePreChatFields,
    });

    store.setTouchpoint(touchpoint);
    store.setPreChatFields(preChatFields);
    store.setShowChatButtonOnLoad(showChatButtonOnLoad);

    if (store.chatType === 'SIERRA') {
        initSierraChat({
            environment,
            user,
            seller,
            isSellerChat,
            touchpoint,
            locale,
            preChatFields,
            openWindow,
            setIsInitialized: store.setIsInitialized,
        });
    }

    if (openWindow) {
        store.setIsWindowVisible(true);
        store.setIsButtonVisible(false);
    } else if (showChatButtonOnLoad && !store.isWindowVisible) {
        store.setIsButtonVisible(showChatButtonOnLoad);
    }
}

export { initSupportChat, type InitSupportChatOptions };
